import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import { message } from "antd";

// ✅ Location API Response Type
export interface Location {
  id: number;
  name: string;
  address: string;
  code: string;
  minimum_order_amount: number;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

// ✅ Fetch All Locations (Simple version for dropdowns - maintains backward compatibility)
export const useLocations = () => {
  return useQuery({
    queryKey: ["locations"],
    queryFn: async () => {
      const response = await apiClient.get<{
        status: boolean;
        data: Location[];
      }>("/location");
      return response.data.data; // ✅ Extract location data only
    },
    staleTime: 1000 * 60 * 60, // ✅ Cache for 1 hour
    refetchOnWindowFocus: false,
  });
};

// ✅ Fetch Locations with Pagination and Filters (for management page)
export const useLocationsPaginated = (
  page: number = 1,
  filters: Record<string, any> = {}
) => {
  return useQuery({
    queryKey: ["locationsPaginated", page, filters],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== null && v !== "")
        ),
      }).toString();

      const response = await apiClient.get<{
        status: boolean;
        data: Location[];
        meta: { total: number; current_page: number; last_page: number };
      }>(`/location?${queryParams}`);
      return response.data;
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    refetchOnWindowFocus: false,
  });
};

// ✅ Fetch Single Location by ID
export const useLocationById = (locationId?: number) => {
  return useQuery({
    queryKey: ["location", locationId],
    queryFn: async () => {
      if (!locationId) return null;
      const response = await apiClient.get<{
        status: boolean;
        data: Location;
      }>(`/location/${locationId}`);
      return response.data.data;
    },
    enabled: !!locationId, // ✅ Only run if locationId exists
    staleTime: 1000 * 60 * 10,
  });
};

// ✅ Fetch Single User Location (maintains backward compatibility)
export const useUserLocation = (locationId?: number) => {
  return useQuery({
    queryKey: ["userLocation", locationId],
    queryFn: async () => {
      if (!locationId) return null;
      const response = await apiClient.get<{ status: boolean; data: Location }>(
        `/location/${locationId}`
      );
      return response.data.data.name; // ✅ Extract location name
    },
    enabled: !!locationId, // ✅ Only run if locationId exists
  });
};

// ✅ Mutation for Creating a Location
export const useCreateLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (locationData: {
      name: string;
      address: string;
      code: string;
      minimum_order_amount: number;
    }) => {
      const response = await apiClient.post("/location/create", locationData);
      return response.data;
    },
    onSuccess: () => {
      message.success("Location created successfully!");
      queryClient.invalidateQueries({ queryKey: ["locations"] });
      queryClient.invalidateQueries({ queryKey: ["locationsPaginated"] });
    },
    onError: (error: any) => {
      console.error("Location creation error:", error);

      // Check for duplicate code error
      if (
        error?.response?.data?.message?.toLowerCase().includes("duplicate") &&
        error?.response?.data?.message?.toLowerCase().includes("code")
      ) {
        message.error(
          "Location code already exists. Please use a different code."
        );
      } else {
        message.error("Failed to create location. Please try again.");
      }
    },
  });
};

// ✅ Mutation for Updating a Location
export const useUpdateLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      locationId,
      locationData,
    }: {
      locationId: number;
      locationData: {
        name: string;
        address: string;
        code: string;
        minimum_order_amount: number;
      };
    }) => {
      const response = await apiClient.put(
        `/location/${locationId}`,
        locationData
      );
      return response.data;
    },
    onSuccess: () => {
      message.success("Location updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["locations"] });
      queryClient.invalidateQueries({ queryKey: ["locationsPaginated"] });
      queryClient.invalidateQueries({ queryKey: ["location"] });
      queryClient.invalidateQueries({ queryKey: ["userLocation"] });
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    },
    onError: (error: any) => {
      console.error("Location update error:", error);

      // Check for duplicate code error
      if (
        error?.response?.data?.message?.toLowerCase().includes("duplicate") &&
        error?.response?.data?.message?.toLowerCase().includes("code")
      ) {
        message.error(
          "Location code already exists. Please use a different code."
        );
      } else {
        message.error("Failed to update location. Please try again.");
      }
    },
  });
};

// ✅ Mutation for Deleting a Location
export const useDeleteLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (locationId: number) => {
      const response = await apiClient.delete(`/location/${locationId}`);
      return response.data;
    },
    onSuccess: () => {
      message.success("Location deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["locations"] });
      queryClient.invalidateQueries({ queryKey: ["locationsPaginated"] });
    },
    onError: (error: any) => {
      console.error("Location deletion error:", error);

      // Check if location is being used by other entities
      if (
        error?.response?.data?.message?.toLowerCase().includes("constraint") ||
        error?.response?.data?.message?.toLowerCase().includes("foreign key")
      ) {
        message.error(
          "Cannot delete location as it is being used by other records."
        );
      } else {
        message.error("Failed to delete location. Please try again.");
      }
    },
  });
};

// ✅ Mutation Hook for Updating User Location (maintains backward compatibility)
export const useUpdateUserLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (locationId: number) => {
      await apiClient.put(`/location/${locationId}`, { id: locationId }); // ✅ Send PUT request
    },
    onSuccess: () => {
      message.success("Location updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["currentUser"] }); // ✅ Refresh user data
      queryClient.invalidateQueries({ queryKey: ["userLocation"] }); // ✅ Refresh location data
    },
    onError: () => {
      message.error("Failed to update location");
    },
  });
};
